import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  LineChart,
  Line,
  Cell,
} from "recharts";
import "primereact/resources/themes/lara-light-blue/theme.css";
import Layout from "../../component/layout";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../app/z-store/store";

function ReportsAndAnalysis() {
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile-1");

  // Data states
  const [expenses, setExpenses] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [budgetAnalysis, setBudgetAnalysis] = useState([]);

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  useEffect(() => {
    if (selectedFacilityId) {
      fetchAllData();
    }
  }, [selectedFacilityId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchExpenses(),
        fetchBudgets(),
        fetchCategories()
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    try {
      const res = await makeRequest2(`/api/app/expense_management/expenses/get_all/${selectedFacilityId}`, 'GET');
      const expenseData = res?.data?.expenses || [];
      setExpenses(expenseData);
      processExpenseData(expenseData);
    } catch (err) {
      console.error("Error fetching expenses:", err);
    }
  };

  const fetchBudgets = async () => {
    try {
      const res = await makeRequest2(`/api/app/expense_management/get_budgets/${selectedFacilityId}`, 'GET');
      const budgetData = res?.data?.budgets || [];
      setBudgets(budgetData);
      processBudgetData(budgetData);
    } catch (err) {
      console.error("Error fetching budgets:", err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await makeRequest2(`/api/app/expense_management/expense_category/get_all_expense_categories/${selectedFacilityId}`, 'GET');
      const categoryData = res?.data?.data || [];
      setCategories(categoryData);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const processExpenseData = (expenseData) => {
    // Process monthly data
    const monthlyExpenses = {};
    const categoryExpenses = {};

    expenseData.forEach(expense => {
      const date = new Date(expense.date || expense.createdAt);
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

      // Monthly aggregation
      if (!monthlyExpenses[monthYear]) {
        monthlyExpenses[monthYear] = {
          month: monthName,
          expenses: 0,
          count: 0
        };
      }
      monthlyExpenses[monthYear].expenses += expense.amount || 0;
      monthlyExpenses[monthYear].count += 1;

      // Category aggregation
      const categoryName = getCategoryName(expense);
      if (!categoryExpenses[categoryName]) {
        categoryExpenses[categoryName] = 0;
      }
      categoryExpenses[categoryName] += expense.amount || 0;
    });

    // Convert to array format for charts
    const monthlyArray = Object.values(monthlyExpenses)
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-6); // Last 6 months

    const categoryArray = Object.entries(categoryExpenses)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    setMonthlyData(monthlyArray);
    setCategoryData(categoryArray);
  };

  const processBudgetData = (budgetData) => {
    const analysis = budgetData.map(budget => ({
      name: budget.budgetName,
      allocation: budget.allocation || 0,
      used: budget.used || 0,
      remaining: (budget.allocation || 0) - (budget.used || 0),
      percentage: budget.allocation ? ((budget.used || 0) / budget.allocation) * 100 : 0,
      status: budget.approvalStatus || 'pending',
      department: budget.department || 'Unknown'
    }));

    setBudgetAnalysis(analysis);
  };

  const getCategoryName = (expense) => {
    if (expense.CategoryInfo && expense.CategoryInfo.name) {
      return expense.CategoryInfo.name;
    }
    const category = categories.find(cat => cat._id === expense.categoryId);
    return category?.name || 'Uncategorized';
  };

  const getBudgetName = (expense) => {
    if (expense.BudgetInfo && expense.BudgetInfo.name) {
      return expense.BudgetInfo.name;
    }
    const budget = budgets.find(bud => bud._id === expense.budgetId);
    return budget?.budgetName || 'Unknown Budget';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount || 0);
  };

  const calculateTotalExpenses = () => {
    return expenses.reduce((total, expense) => total + (expense.amount || 0), 0);
  };

  const calculateTotalBudget = () => {
    return budgets.reduce((total, budget) => total + (budget.allocation || 0), 0);
  };

  const calculateMonthlyAverage = () => {
    if (monthlyData.length === 0) return 0;
    const total = monthlyData.reduce((sum, month) => sum + month.expenses, 0);
    return total / monthlyData.length;
  };

  const calculateBudgetUtilization = () => {
    const totalBudget = calculateTotalBudget();
    const totalExpenses = calculateTotalExpenses();
    return totalBudget > 0 ? (totalExpenses / totalBudget) * 100 : 0;
  };

  const getFilteredExpenses = () => {
    let filtered = expenses;

    if (searchText) {
      filtered = filtered.filter(expense =>
        expense.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        expense.description?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (startDate && endDate) {
      filtered = filtered.filter(expense => {
        const expenseDate = new Date(expense.date || expense.createdAt);
        return expenseDate >= new Date(startDate) && expenseDate <= new Date(endDate);
      });
    }

    return filtered;
  };

  const filteredExpenses = getFilteredExpenses();

  const totalExpenses = calculateTotalExpenses();
  const totalBudget = calculateTotalBudget();
  const monthlyAverage = calculateMonthlyAverage();
  const budgetUtilization = calculateBudgetUtilization();

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body text-center">
                <h5>No Facility Selected</h5>
                <p>Please select a facility to view reports and analysis.</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={"/facility/"}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={"#"}>Expense Management</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={"/facility/expense_management/reports"}>
                    Reports & Analysis
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body py-0">
              <ul
                className="nav nav-tabs profile-tabs"
                id="myTab"
                role="tablist"
              >
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "profile-1" ? "active" : ""}`}
                    onClick={() => handleTabClick("profile-1")}
                    type="button"
                  >
                    <i className="ti ti-rotate-2 me-2"></i>
                    Comprehensive breakdown of costs
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "profile-2" ? "active" : ""}`}
                    onClick={() => handleTabClick("profile-2")}
                    type="button"
                  >
                    <i className="ti ti-report-money me-2"></i>
                    Budget report
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "profile-3" ? "active" : ""}`}
                    onClick={() => handleTabClick("profile-3")}
                    type="button"
                  >
                    <i className="ti ti-file-minus me-2"></i>
                    Summaries per expense category
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "profile-4" ? "active" : ""}`}
                    onClick={() => handleTabClick("profile-4")}
                    type="button"
                  >
                    <i className="ti ti-file-analytics me-2"></i>
                    Graphs & Dashboards
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="tab-content" id="myTabContent">
            {/* Cost Breakdown Tab */}
            <div
              className={`tab-pane fade ${activeTab === "profile-1" ? "show active" : ""}`}
              id="profile-1"
            >
              <div className="row">
                <div className="col-lg-12 col-xxl-12">
                  <div className="card">
                    <div className="card-body position-relative">
                      {loading ? (
                        <div className="text-center p-4">
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ height: "400px" }} className="mb-4">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={monthlyData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Legend />
                                <Bar
                                  dataKey="expenses"
                                  fill="#0d6efd"
                                  name="Monthly Expenses"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="row">
                            <div className="col-md-3">
                              <div className="card bg-primary text-white">
                                <div className="card-body">
                                  <h6>Total Expenses</h6>
                                  <h3>{formatCurrency(totalExpenses)}</h3>
                                  <small>{expenses.length} transactions</small>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="card bg-success text-white">
                                <div className="card-body">
                                  <h6>Monthly Average</h6>
                                  <h3>{formatCurrency(monthlyAverage)}</h3>
                                  <small>Based on {monthlyData.length} months</small>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="card bg-warning text-white">
                                <div className="card-body">
                                  <h6>Budget Utilization</h6>
                                  <h3>{budgetUtilization.toFixed(1)}%</h3>
                                  <small>Of total budget</small>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="card bg-info text-white">
                                <div className="card-body">
                                  <h6>Total Budget</h6>
                                  <h3>{formatCurrency(totalBudget)}</h3>
                                  <small>{budgets.length} budgets</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Budget Report Tab */}
            <div
              className={`tab-pane fade ${activeTab === "profile-2" ? "show active" : ""}`}
              id="profile-2"
            >
              <div className="row">
                <div className="col-lg-12 col-xxl-12">
                  <div className="card">
                    <div className="card-body position-relative">
                      {loading ? (
                        <div className="text-center p-4">
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ height: "400px" }} className="mb-4">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={budgetAnalysis}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Legend />
                                <Bar
                                  dataKey="allocation"
                                  fill="#dc3545"
                                  name="Budget Allocation"
                                />
                                <Bar
                                  dataKey="used"
                                  fill="#0d6efd"
                                  name="Used Amount"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="row">
                            <div className="col-md-3">
                              <div className="card bg-primary text-white">
                                <div className="card-body">
                                  <h6>Total Budget</h6>
                                  <h3>{formatCurrency(totalBudget)}</h3>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="card bg-success text-white">
                                <div className="card-body">
                                  <h6>Used Budget</h6>
                                  <h3>{formatCurrency(budgetAnalysis.reduce((sum, budget) => sum + budget.used, 0))}</h3>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="card bg-warning text-white">
                                <div className="card-body">
                                  <h6>Remaining</h6>
                                  <h3>{formatCurrency(budgetAnalysis.reduce((sum, budget) => sum + budget.remaining, 0))}</h3>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="card bg-info text-white">
                                <div className="card-body">
                                  <h6>Avg Utilization</h6>
                                  <h3>{budgetAnalysis.length > 0 ? (budgetAnalysis.reduce((sum, budget) => sum + budget.percentage, 0) / budgetAnalysis.length).toFixed(1) : 0}%</h3>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Category Summaries Tab */}
            <div
              className={`tab-pane fade ${activeTab === "profile-3" ? "show active" : ""}`}
              id="profile-3"
            >
              <div className="row">
                <div className="col-lg-12 col-xxl-12">
                  <div className="card">
                    <div className="card-header">
                      <div className="row">
                        <div className="col-md-3 col-xs-12 mt-3">
                          <input
                            className="form-control"
                            placeholder="Search expenses..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                        </div>
                        <div className="col-md-8 col-xs-12 mt-2">
                          <div className="row">
                            <div className="col-md-4 col-xs-5 mt-2">
                              <label>Start Date</label>
                              <input
                                className="form-control"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                              />
                            </div>
                            <div className="col-md-4 col-xs-5 mt-2">
                              <label>End Date</label>
                              <input
                                className="form-control"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {loading ? (
                        <div className="text-center p-4">
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ height: "400px" }} className="mb-4">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={categoryData}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={150}
                                  label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                                >
                                  {categoryData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="row">
                            {categoryData.slice(0, 4).map((category, index) => (
                              <div key={index} className="col-md-3 mb-4">
                                <div
                                  className={`card bg-${
                                    index === 0
                                      ? "primary"
                                      : index === 1
                                      ? "success"
                                      : index === 2
                                      ? "warning"
                                      : "info"
                                  } text-white`}
                                >
                                  <div className="card-body">
                                    <h6>{category.name}</h6>
                                    <h3>{formatCurrency(category.value)}</h3>
                                    <small>
                                      {categoryData.length > 0 ? (
                                        (category.value / categoryData.reduce((acc, curr) => acc + curr.value, 0) * 100).toFixed(1)
                                      ) : 0}% of total
                                    </small>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Graphs & Dashboards Tab */}
            <div
              className={`tab-pane fade ${activeTab === "profile-4" ? "show active" : ""}`}
              id="profile-4"
            >
              <div className="row">
                <div className="col-md-6 mb-4">
                  <div className="card">
                    <div className="card-header">
                      <h5>Monthly Expense Trends</h5>
                    </div>
                    <div className="card-body">
                      <div style={{ height: "300px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="expenses"
                              stroke="#0d6efd"
                              name="Monthly Expenses"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 mb-4">
                  <div className="card">
                    <div className="card-header">
                      <h5>Category Distribution</h5>
                    </div>
                    <div className="card-body">
                      <div style={{ height: "300px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={categoryData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              label={({percent}) => `${(percent * 100).toFixed(0)}%`}
                            >
                              {categoryData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header">
                      <h5>Budget vs Actual Analysis</h5>
                    </div>
                    <div className="card-body">
                      <div style={{ height: "300px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={budgetAnalysis}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Bar
                              dataKey="allocation"
                              fill="#dc3545"
                              name="Budget Allocation"
                            />
                            <Bar
                              dataKey="used"
                              fill="#0d6efd"
                              name="Used Amount"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ReportsAndAnalysis;